import { useCallback } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { useHintHover } from "@/components/hints/useHintHover";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { useStore } from "@/store";
import { getIsMobile } from "@/utils/getIsMobile";
import { displayHotkey } from "@/utils/hotkeys";

// iOS only opens the keyboard for focus() inside the tap, but SearchInput mounts and
// focuses later. A throwaway input holds the keyboard open until it takes over.
function holdMobileKeyboard() {
  const holder = document.createElement("input");
  holder.setAttribute("aria-hidden", "true");
  holder.style.cssText = "position:fixed;top:0;left:0;width:0;height:0;opacity:0;font-size:16px";
  document.body.appendChild(holder);
  holder.focus();
  setTimeout(() => holder.remove(), 1000);
}

export const SearchButton = observer(function SearchButton({
  className,
  wrapperClassName,
  ref,
}: {
  className?: string;
  wrapperClassName?: string;
  ref?: React.Ref<HTMLButtonElement>;
}) {
  const { search, view } = useStore();
  const t = useTranslations();

  const shortcutText = displayHotkey("search");
  const hoverHandlers = useHintHover("search", { shortcut: shortcutText });

  const handleClick = useCallback(() => {
    if (getIsMobile()) holdMobileKeyboard();
    view.toggleLeftSidebar(true);
    search.open();
  }, [search, view]);

  return (
    <Sidebar.Button
      title={t("search.button")}
      hotkey="search"
      icon={<Icon name="Search" className="sm:w-[28px] sm:h-[28px]" />}
      onClick={handleClick}
      ref={ref}
      className={className}
      wrapperClassName={wrapperClassName}
      {...hoverHandlers}
    />
  );
});
